import { useState } from 'react';
import { StickyNote, Plus, Lock, Search, X, Pencil, Trash2, Table2, Rows3 } from 'lucide-react';
import { FormModal } from '../../components/ui/FormModal';
import { Button, Toast } from '../../components/ui/FormField';
import { Field, TextInput, TextareaInput, PrimaryButton, GhostButton } from '../../components/ui/Field';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { IconButton, Cell, PaginationControls, GradientOverlayActions, ViewModeSwitch } from '../../components/ui/grid';
import { useCrudPage } from '../../hooks/useCrudPage';
import { useAutoCreate } from '../../hooks/useAutoCreate';
import { usePageHeader } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { annotationApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type { Annotation } from '../../types/api';

type AnnoView = 'comfortable' | 'compact';
const VIEW_MODE_KEY = 'annotations.viewMode';
function loadAnnoView(): AnnoView {
  try { const v = localStorage.getItem(VIEW_MODE_KEY); if (v === 'compact' || v === 'comfortable') return v; } catch { /* ignore */ }
  return 'comfortable';
}

type UseFilter = 'all' | 'inuse' | 'available';

const empty: Partial<Annotation> = { shortDescription: '', longDescription: '' };

export function AnnotationsPage() {
  const authUser = useAuthStore((s) => s.user);
  const canEdit = authUser?.role === 'SuperAdmin' || authUser?.role === 'CompanyAdmin';
  const recordActivity = useActivityStore((s) => s.record);

  const crud = useCrudPage<Annotation>({ fetchFn: annotationApi.getAll });
  const [form, setForm] = useState<Partial<Annotation>>(empty);
  const [saving, setSaving] = useState(false);
  const [isAnnotationInUse, setIsAnnotationInUse] = useState(false);
  const [shortDescError, setShortDescError] = useState('');
  const [longDescError, setLongDescError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [viewMode, setViewModeRaw] = useState<AnnoView>(loadAnnoView);
  const setViewMode = (v: AnnoView) => { setViewModeRaw(v); try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* ignore */ } };
  const compact = viewMode === 'compact';
  const [useFilter, setUseFilter] = useState<UseFilter>('all');

  const openEdit = (item: Annotation) => {
    setForm({ ...item });
    setIsAnnotationInUse(!!item.isInUse);
    setShortDescError('');
    setLongDescError('');
    setSubmitted(false);
    crud.openEdit(item);
  };
  const openCreate = () => { setForm({ ...empty }); setIsAnnotationInUse(false); setShortDescError(''); setLongDescError(''); setSubmitted(false); crud.openCreate(); };
  useAutoCreate(openCreate, canEdit);

  const handleSave = async () => {
    setSubmitted(true);
    let hasError = false;
    if (!form.shortDescription?.trim()) { setShortDescError('Short Description is required'); hasError = true; } else { setShortDescError(''); }
    if (!form.longDescription?.trim()) { setLongDescError('Long Description is required'); hasError = true; } else { setLongDescError(''); }
    if (hasError) return;
    setSaving(true);
    try {
      const label = form.shortDescription?.trim() || 'Annotation';
      if (crud.editingItem) {
        await annotationApi.update(form);
        crud.showToast('Annotation updated');
        recordActivity({ type: 'update', entity: 'Annotations', label, user: authUser?.username });
      } else {
        await annotationApi.create(form);
        crud.showToast('Annotation created');
        recordActivity({ type: 'create', entity: 'Annotations', label, user: authUser?.username });
      }
      crud.closeModal(); crud.load();
    } catch { crud.showToast('Failed to save annotation', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item: Annotation) => {
    // Client-side guard (server enforces this too).
    if (item.isInUse) {
      crud.showToast('This annotation is in use by one or more wells and cannot be deleted.', 'error');
      return;
    }
    if (!confirm('Delete this annotation?')) return;
    try {
      const resp = await annotationApi.delete(item.id);
      if (resp?.status === 'Failed') {
        crud.showToast(resp.error?.errorMessage || 'Failed to delete', 'error');
        return;
      }
      crud.showToast('Deleted');
      recordActivity({ type: 'delete', entity: 'Annotations', label: item.shortDescription?.trim() || `Annotation #${item.id}`, user: authUser?.username });
      crud.load();
    }
    catch { crud.showToast('Failed to delete', 'error'); }
  };

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  usePageHeader(
    'Annotations',
    canEdit ? <Button onClick={openCreate}><Plus size={16} /> Add Annotation</Button> : null,
    [canEdit],
  );

  const filteredData = crud.data.filter((a) => {
    if (useFilter === 'inuse' && !a.isInUse) return false;
    if (useFilter === 'available' && a.isInUse) return false;
    return true;
  });
  const filtersActive = useFilter !== 'all';

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  const colTemplate = compact
    ? 'minmax(220px,1.3fr) minmax(300px,2fr) 120px'
    : (canEdit
        ? 'minmax(200px,1.2fr) minmax(280px,2fr) 120px 116px'
        : 'minmax(200px,1.2fr) minmax(280px,2fr) 120px');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, width: 'fit-content', minWidth: '100%' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 8, width: 280 }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search annotations…"
              value={crud.searchText}
              onChange={(e) => { crud.setSearchText(e.target.value); crud.setOffset(0); }}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13.5, flex: 1, fontFamily: 'var(--font-body)', color: 'var(--color-text)', width: '100%' }}
            />
            {crud.searchText && (
              <button onClick={() => { crud.setSearchText(''); crud.setOffset(0); }} aria-label="Clear search" title="Clear search"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 9, padding: 0, background: 'transparent', border: 'none', color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0 }}>
                <X size={13} />
              </button>
            )}
          </div>

          <InUseSegmented value={useFilter} onChange={setUseFilter} />

          <div style={{ flex: 1 }} />

          <ViewModeSwitch
            value={viewMode}
            onChange={setViewMode}
            options={[{ id: 'comfortable', tip: 'Comfortable view', Icon: Table2 }, { id: 'compact', tip: 'Compact view', Icon: Rows3 }]}
          />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading ? 'Loading…' : filtersActive ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total` : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header + rows */}
        <div >
          <div
            className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
            style={{
              display: 'grid',
              gridTemplateColumns: colTemplate,
              columnGap: 0,
              padding: 0,
              alignItems: 'stretch',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              position: 'sticky',
              top: 72,
              zIndex: 10,
              fontSize: 14,
              letterSpacing: 0.3,
              color: 'var(--color-text)',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
            }}
          >
            <SortHeader label="Short Description" col="ShortDescription" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Long Description" col="LongDescription" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="In Use" col="IsInUse" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            {!compact && canEdit && <div style={{ textAlign: 'right' }}>Actions</div>}
          </div>

          {crud.loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>Loading…</div>
          ) : filteredData.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <StickyNote size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>{filtersActive ? 'No annotations match this filter' : 'No annotations found'}</div>
              <div style={{ fontSize: 12 }}>{filtersActive ? 'Try a different filter.' : 'Add a new annotation to get started.'}</div>
            </div>
          ) : (
            <div>
              {filteredData.map((a, i) => (
                <AnnotationRow
                  key={a.id ?? i}
                  annotation={a}
                  compact={compact}
                  canEdit={canEdit}
                  colTemplate={colTemplate}
                  isLast={i === filteredData.length - 1}
                  onEdit={() => openEdit(a)}
                  onDelete={() => handleDelete(a)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination footer */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--color-text-dim)', background: 'var(--color-panel-alt)', borderRadius: '0 0 10px 10px' }}>
          <div>
            Showing <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> annotations
          </div>
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPage={(p) => crud.setOffset((p - 1) * crud.pageSize)} />
        </div>
      </div>

      {/* Form modal — flat; read-only when the annotation is in use */}
      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        title={crud.editingItem ? 'Edit Annotation' : 'New Annotation'}
        icon={<StickyNote size={20} />}
        footer={
          isAnnotationInUse ? (
            <GhostButton onClick={crud.closeModal}>Back</GhostButton>
          ) : (
            <>
              <GhostButton onClick={crud.closeModal} disabled={saving}>Cancel</GhostButton>
              <PrimaryButton onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : crud.editingItem ? 'Save changes' : 'Add Annotation'}
              </PrimaryButton>
            </>
          )
        }
      >
        {isAnnotationInUse && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: 'rgba(183,121,31,0.08)', border: '1px solid rgba(183,121,31,0.22)' }}>
            <Lock size={15} style={{ color: 'var(--color-warn)', flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-warn)' }}>This annotation is used by wells and is read-only.</span>
          </div>
        )}
        <Field label="Short Description" required error={submitted ? shortDescError : undefined}>
          <TextInput
            value={form.shortDescription ?? ''}
            placeholder="e.g. Casing shoe"
            readOnly={isAnnotationInUse}
            error={submitted && !!shortDescError}
            onChange={(e) => { setForm(p => ({ ...p, shortDescription: e.target.value })); if (submitted && e.target.value.trim()) setShortDescError(''); }}
          />
        </Field>
        <Field label="Long Description" required error={submitted ? longDescError : undefined}>
          <TextareaInput
            value={form.longDescription ?? ''}
            placeholder="Describe what this annotation marks…"
            readOnly={isAnnotationInUse}
            error={submitted && !!longDescError}
            onChange={(e) => { setForm(p => ({ ...p, longDescription: e.target.value })); if (submitted && e.target.value.trim()) setLongDescError(''); }}
          />
        </Field>
      </FormModal>

      {crud.toast && <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

function InUseBadge({ inUse }: { inUse: boolean }) {
  if (!inUse) return <span style={{ color: 'var(--color-text-dim)', fontSize: 13 }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 12, background: 'rgba(183,121,31,0.18)', border: '1px solid rgba(183,121,31,0.45)', color: 'var(--color-warn)', fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-body)' }}>
      <Lock size={12} /> In Use
    </span>
  );
}

function AnnotationRow({
  annotation: a,
  compact,
  canEdit,
  colTemplate,
  isLast,
  onEdit,
  onDelete,
}: {
  annotation: Annotation;
  compact: boolean;
  canEdit: boolean;
  colTemplate: string;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const rowBg = hover ? 'var(--color-panel-alt)' : (compact ? 'var(--color-panel)' : 'transparent');
  const wrap = { overflowWrap: 'anywhere' as const, wordBreak: 'break-word' as const };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
      style={{
        display: 'grid',
        gridTemplateColumns: colTemplate,
        columnGap: 0,
        padding: 0,
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'stretch',
        fontSize: 14,
        position: compact ? 'relative' : undefined,
        background: rowBg,
        transition: 'background 120ms',
      }}
    >
      <Cell style={{ ...wrap, fontWeight: 500 }}>{a.shortDescription || '—'}</Cell>
      <Cell style={{ ...wrap, color: 'var(--color-text-mid)' }}>{a.longDescription || '—'}</Cell>
      <div><InUseBadge inUse={!!a.isInUse} /></div>

      {!compact && canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <IconButton title="Edit" onClick={onEdit}><Pencil size={17} /></IconButton>
          <IconButton title="Delete" onClick={onDelete} hoverDanger><Trash2 size={17} /></IconButton>
        </div>
      )}
      {compact && canEdit && (
        <GradientOverlayActions visible={hover} bgColor={rowBg} onEdit={onEdit} onDelete={onDelete} />
      )}
    </div>
  );
}

function InUseSegmented({ value, onChange }: { value: UseFilter; onChange: (v: UseFilter) => void }) {
  const opts: { v: UseFilter; label: string; dot?: string }[] = [
    { v: 'all', label: 'All' },
    { v: 'inuse', label: 'In use', dot: 'var(--color-warn)' },
    { v: 'available', label: 'Available', dot: 'var(--color-good)' },
  ];
  return (
    <div style={{ display: 'inline-flex', padding: 3, background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 8, gap: 2 }}>
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', border: 'none', borderRadius: 6,
              fontSize: 12.5, fontWeight: 500, fontFamily: 'var(--font-body)', cursor: 'pointer',
              background: active ? 'var(--color-panel)' : 'transparent',
              color: active ? 'var(--color-text)' : 'var(--color-text-mid)',
              boxShadow: active ? '0 1px 2px rgba(15,23,32,0.06)' : 'none',
              transition: 'all 120ms',
            }}
          >
            {o.dot && <span style={{ width: 6, height: 6, borderRadius: 3, background: o.dot, display: 'inline-block' }} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
